import {
	AfterViewInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ContentChildren,
	EventEmitter,
	Input,
	OnChanges,
	OnInit,
	Output,
	QueryList,
	SimpleChanges,
	TemplateRef,
	ViewChildren,
} from '@angular/core';
import { ApiResponse } from '@app/infrastructure/data-list/grid/api-grid/api';
import {
	IAdaptedGridColumnConfig,
	IGridColumnConfig,
} from '@app/infrastructure/data-list/grid/grid-column-config.model';
import {
	Grid,
	GridType,
	ICustomCells,
	ICustomHeader,
	identityByPropFn,
	IRowConfig,
	ItemIdentityFn,
	RowCustomClassesFn,
	TemplateTypes,
} from '@app/infrastructure/data-list/grid/grid.model';
import {
	DEFAULT_PAGE_SIZE,
	FIRST_PAGE,
	PaginateConfig,
} from '@app/infrastructure/data-list/pagination/pagination.model';
import {
	CollapsibleRow,
	CollapsibleRowDirective,
} from '@app/infrastructure/data-list/table/directives/collapsible-row.directive';
import { CustomCell, CustomCellDirective } from '@app/infrastructure/data-list/table/directives/custom-cell.directive';
import {
	CustomRow,
	CustomRowDirective,
	isInsertAtIndexFn,
} from '@app/infrastructure/data-list/table/directives/custom-row.directive';
import { combineLatest, Observable, of, ReplaySubject } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

@Component({
	selector: 'app-grid',
	templateUrl: './grid.component.html',
	styleUrls: ['./grid.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GridComponent<T extends { id: string }> implements AfterViewInit, OnInit, OnChanges {
	@Input() public columns: IGridColumnConfig<T>[] = [];
	@Input() public listAdapter?: Grid<T, unknown>;
	@Input() public pageSize: number = DEFAULT_PAGE_SIZE;
	@Input() public type: GridType = GridType.default;
	@Input() public isEnableGlobalCollapseButton = false;
	@Input() public uniqKey: keyof T = 'id';
	@Input() public isStickyHeader = false;
	@Input() public isHideTableHead = false;
	@Input() public customRowClassesFn?: RowCustomClassesFn;

	@Output() public rowClick = new EventEmitter<T>();

	@ContentChildren(CustomCellDirective, { read: CustomCell })
	public customCells: QueryList<CustomCell> = new QueryList();

	@ContentChildren(CustomRowDirective, { read: CustomRow })
	public customRows: QueryList<CustomRow> = new QueryList<CustomRow>();

	@ContentChildren(CollapsibleRowDirective, { read: CollapsibleRow })
	public collapsibleRows: QueryList<CollapsibleRow> = new QueryList<CollapsibleRow>();

	@ViewChildren(CustomCellDirective, { read: CustomCell })
	public customDefaultCells: QueryList<CustomCell> = new QueryList();

	public readonly collapseCellWidth = '40px';
	public columnsCount = 0;
	public isRowCollapsed: Record<string, boolean> = {};
	public adaptedColumns: IAdaptedGridColumnConfig<T>[] = [];
	public customRowCells = ICustomCells;
	public customHeaderCells = ICustomHeader;
	public identityKeyFn = identityByPropFn<{ id: string }>('id');
	public totalCount = FIRST_PAGE;
	public page = FIRST_PAGE;
	public data: T[] = [];
	public rowsConfigs$: Observable<IRowConfig<T>[]> = of([]);
	public defaultRowsUpdated$ = new ReplaySubject<IRowConfig<T>[]>();
	private cellTemplates: Record<string, TemplateRef<unknown>> = {};
	private cellDefaultTemplates: Record<string, TemplateRef<unknown>> = {};
	private collapsibleRowTemplates: Record<string, TemplateRef<unknown>> = {};

	public get isClickUsed(): boolean {
		return !!this.rowClick?.observers?.length;
	}

	public get isAllRowOpen(): boolean {
		return !Object.values(this.isRowCollapsed).some((collapseStatus) => collapseStatus);
	}

	constructor(protected cdRef: ChangeDetectorRef) {}

	public changesTracker: ItemIdentityFn<IRowConfig<T>> = (_: number, entity: IRowConfig<T>) => entity.id;

	public ngOnInit(): void {
		if (!this.listAdapter) {
			return;
		}

		this.changesTracker = (_: number, entity: IRowConfig<T>): string | number =>
			this.listAdapter?.identityFn(_, entity.data) || entity.id;
		this.trackDataChanges(this.listAdapter.changes());
	}

	public ngAfterViewInit(): void {
		this.collapsibleRowTemplates = this.getCollapsibleRowTemplates();
		this.isRowCollapsed = this.getCollapseRowStatuses();
		this.cellTemplates = this.geCellTemplates(TemplateTypes.outer);
		this.cellDefaultTemplates = this.geCellTemplates(TemplateTypes.inner);
		this.adaptedColumns = this.adaptColumns(this.columns);
		this.getColumnsCount();

		this.rowsConfigs$ = combineLatest([
			this.defaultRowsUpdated$.asObservable(),
			this.collapsibleRows.changes.pipe(startWith(this.collapsibleRows)),
			this.customRows.changes.pipe(startWith(this.customRows)),
		]).pipe(
			map(([defaultRows, collapsibleRows, customRows]) => this.adaptAllRows(defaultRows, collapsibleRows, customRows)),
		);

		this.cdRef.detectChanges();
	}

	public ngOnChanges({ columns }: SimpleChanges): void {
		if (!columns?.firstChange) {
			this.adaptedColumns = this.adaptColumns(this.columns);
			this.getColumnsCount();
		}
	}

	public getColumnsCount(): void {
		let additionalColumns = 1;

		this.columnsCount = this.adaptedColumns.length + additionalColumns;
	}

	public rowClickHandler(config: IRowConfig<T> | undefined): void {
		if (config?.id && this.isRowCanCollapse(config.id)) {
			this.toggleTargetRowCollapse(config.id);
		}

		this.rowClick.emit(config.data);
	}

	public isRowCanCollapse(rowId: string): boolean {
		return this.isRowCollapsed[rowId] !== undefined;
	}

	public changePagination({ pageNumber, pageSize }: PaginateConfig): void {
		if (!this.listAdapter) {
			return;
		}
		this.listAdapter.setSlice(pageNumber, pageSize || Number(DEFAULT_PAGE_SIZE));
	}

	public getValue(row: T, prop: unknown): unknown {
		if (!(prop as string).includes('.')) {
			return row[prop as keyof T];
		}

		// @ts-ignore
		return (prop as string).split('.').reduce<unknown>((value, key) => value && value[key], row);
	}

	public toggleAllRowCollapse(): void {
		const statusToSet = this.isAllRowOpen;

		Object.keys(this.isRowCollapsed).forEach((rowKey) => {
			this.isRowCollapsed[rowKey] = statusToSet;
		});
	}

	public toggleTargetRowCollapse(id: string): void {
		this.isRowCollapsed[id] = !this.isRowCollapsed[id];

		this.cdRef.detectChanges();
	}

	protected adaptDefaultRows(data: T[]): void {
		const defaultRows: IRowConfig<T>[] = data.map((d) => {
			const rowId = (d[this.uniqKey] as unknown as string) || this.getRowId();

			return {
				id: rowId,
				data: d,
			};
		});

		this.defaultRowsUpdated$.next(defaultRows);
	}

	protected adaptAllRows(
		defaultRows: IRowConfig<T>[],
		collapsibleRows: QueryList<CollapsibleRow>,
		customRows: QueryList<CustomRow>,
	): IRowConfig<T>[] {
		this.collapsibleRowTemplates = this.getCollapsibleRowTemplates(collapsibleRows);
		this.isRowCollapsed = this.getCollapseRowStatuses(collapsibleRows);
		const result: IRowConfig<T>[] = [...defaultRows].map((row) => {
			return {
				...row,
				collapsibleTemplateRef: this.collapsibleRowTemplates[row.id],
			};
		});

		const id = this.getRowId();
		customRows.forEach(({ insertAtIndex, tmpRef }: CustomRow) => {
			const index = isInsertAtIndexFn(insertAtIndex) ? insertAtIndex(defaultRows) : insertAtIndex;
			if (typeof index !== 'number' || index < 0) {
				return;
			}
			const customRow: IRowConfig<T> = {
				id,
				customTmpl: tmpRef,
				data: null,
			};
			result.splice(index || 0, 0, customRow);
		});

		return result;
	}

	protected getRowId(): string {
		const radix = 36;
		const length = 9;

		return Math.random().toString(radix).substr(2, length);
	}

	private trackDataChanges(changes: Observable<ApiResponse<T>>): void {
		changes.subscribe(({ totalCount, pageIndex, items, pageSize }) => {
			this.pageSize = pageSize;
			this.totalCount = totalCount;
			this.page = pageIndex;
			this.data = items;
			this.adaptDefaultRows(this.data);
			this.cdRef.markForCheck();
		});
	}

	private adaptColumns(columns: IGridColumnConfig<T>[]): IAdaptedGridColumnConfig<T>[] {
		return columns.map((column) => ({
			...column,
			headerTemplateRef: this.getHeaderTemplate(column),
			cellTemplateRef: this.getCellTemplate(column),
		}));
	}

	private getHeaderTemplate({ headerTemplate }: IGridColumnConfig<T>): TemplateRef<unknown> {
		if (headerTemplate && this.cellTemplates[headerTemplate]) {
			return this.cellTemplates[headerTemplate];
		}

		if (headerTemplate && this.cellDefaultTemplates[headerTemplate]) {
			return this.cellDefaultTemplates[headerTemplate];
		}

		return this.cellDefaultTemplates[ICustomHeader.simple];
	}

	private getCellTemplate({ cellTemplate }: IGridColumnConfig<T>): TemplateRef<unknown> {
		if (cellTemplate && this.cellTemplates[cellTemplate]) {
			return this.cellTemplates[cellTemplate];
		}

		if (cellTemplate && this.cellDefaultTemplates[cellTemplate]) {
			return this.cellDefaultTemplates[cellTemplate];
		}

		return this.cellDefaultTemplates[ICustomCells.simple];
	}

	private geCellTemplates(templateType: TemplateTypes = TemplateTypes.inner): Record<string, TemplateRef<unknown>> {
		const templateTypeMap = {
			[TemplateTypes.inner]: this.customDefaultCells,
			[TemplateTypes.outer]: this.customCells,
		};

		return templateTypeMap[templateType].reduce(
			(templates, { appCustomCell, tmpRef }) => ({
				...templates,
				[appCustomCell]: tmpRef,
			}),
			{},
		);
	}

	private getCollapsibleRowTemplates(rows?: QueryList<CollapsibleRow>): Record<string, TemplateRef<unknown>> {
		return (rows || this.collapsibleRows).reduce((object, row) => {
			object[row.appCollapsibleRow.id] = row.tmpRef as TemplateRef<unknown>;

			return object;
		}, {} as Record<string, TemplateRef<unknown>>);
	}

	private getCollapseRowStatuses(rows?: QueryList<CollapsibleRow>): Record<string, boolean> {
		return (rows || this.collapsibleRows).reduce((object, row) => {
			object[row.appCollapsibleRow.id] = row.appCollapsibleRow.isCollapsed;

			return object;
		}, {} as Record<string, boolean>);
	}
}
